// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { SegmentKey } from '@/billing/lifecycle/segments';
import type { SegmentSummary } from '@/billing/lifecycle/transforms';

// A single segment's placement on the aggregate Gantt's calendar-day
// axis — the anchor-space start/end in days relative to `sent_date = 0`.
// Null when the chain can't produce both anchors (e.g. a missing
// neighboring segment breaks the cumulative position calculation).
export interface SegmentOffsets {
  startDay: number;
  endDay: number;
}

// Compute anchor-space day offsets for each of the six Gantt segments
// from the aggregate segment means. Anchors are:
//   billing_eligible = −(chargeLag + invoiceReview + deliveryLag)
//   drafted          = −(invoiceReview + deliveryLag)
//   opened           = −deliveryLag
//   sent_date        = 0
//   invoice_date     = dateLag (signed; typically negative for backdating)
//   due_date         = dateLag + actualTerms
//   paid_date        = dateLag + actualTerms + delinquency
//
// Returns null for a segment whenever either of its anchors can't be
// computed — e.g. if `deliveryLag` has no observations, none of the ADS
// anchors can be placed on the sent-date-anchored axis, so all three
// ADS segments return null.
export function buildAggregateSegmentOffsets(
  summaries: SegmentSummary[]
): Record<SegmentKey, SegmentOffsets | null> {
  const bySegment: Record<string, number | null> = {};
  for (const s of summaries) {
    bySegment[s.key] = s.avgDays;
  }
  const mkSegment = (
    startKey:
      | 'eligible'
      | 'drafted'
      | 'opened'
      | 'sent'
      | 'invoiceDate'
      | 'due',
    endKey: 'drafted' | 'opened' | 'sent' | 'invoiceDate' | 'due' | 'paid',
    anchors: Record<string, number | null>
  ): SegmentOffsets | null => {
    const start = anchors[startKey];
    const end = anchors[endKey];
    if (start === null || end === null) return null;
    return { startDay: start, endDay: end };
  };

  const deliveryLag = bySegment.deliveryLag;
  const invoiceReview = bySegment.invoiceReview;
  const chargeLag = bySegment.chargeLag;
  const dateLag = bySegment.dateLag;
  const actualTerms = bySegment.actualTerms;
  const delinquency = bySegment.delinquency;

  const anchors: Record<string, number | null> = {
    eligible:
      chargeLag === null || invoiceReview === null || deliveryLag === null
        ? null
        : -(chargeLag + invoiceReview + deliveryLag),
    drafted:
      invoiceReview === null || deliveryLag === null
        ? null
        : -(invoiceReview + deliveryLag),
    opened: deliveryLag === null ? null : -deliveryLag,
    sent: 0,
    invoiceDate: dateLag,
    due:
      dateLag === null || actualTerms === null ? null : dateLag + actualTerms,
    paid:
      dateLag === null || actualTerms === null || delinquency === null
        ? null
        : dateLag + actualTerms + delinquency,
  };

  return {
    chargeLag: mkSegment('eligible', 'drafted', anchors),
    invoiceReview: mkSegment('drafted', 'opened', anchors),
    deliveryLag: mkSegment('opened', 'sent', anchors),
    dateLag: mkSegment('sent', 'invoiceDate', anchors),
    actualTerms: mkSegment('invoiceDate', 'due', anchors),
    delinquency: mkSegment('due', 'paid', anchors),
    totalCycle: null,
  };
}
